import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  Sparkles, Upload, CheckCircle2, FileText, Camera, Loader2,
  ArrowLeft, ArrowRight, ShieldCheck, ClipboardList, Building2,
  Palette, Users, FileCheck2, Send, Info,
} from 'lucide-react';

export default function PartnerOnboardingPublic() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [docs, setDocs] = useState({}); // key -> {url, file_name}
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [acceptDeclaration, setAcceptDeclaration] = useState(false);
  const inputs = useRef({});
  const topRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.publicGetOnboarding(token);
        setData(r);
        setAnswers(r.submitted_data || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Link non valido o già utilizzato');
      } finally { setLoading(false); }
    })();
  }, [token]);

  // Costruisce la lista degli step in modo dinamico
  const steps = useMemo(() => {
    if (!data) return [];
    const questionSteps = (data.questions || []).map((section, i) => ({
      kind: 'section',
      section,
      title: section.section,
      icon: pickSectionIcon(section.section, i),
    }));
    return [
      { kind: 'welcome', title: 'Benvenuto', icon: Sparkles },
      ...questionSteps,
      { kind: 'docs', title: 'Documenti', icon: FileCheck2 },
      { kind: 'review', title: 'Riepilogo & Invio', icon: Send },
    ];
  }, [data]);

  const total = steps.length;
  const current = steps[stepIdx];
  const percent = total > 1 ? Math.round((stepIdx / (total - 1)) * 100) : 0;

  // Scroll in alto al cambio step
  useEffect(() => {
    if (topRef.current) topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [stepIdx]);

  const fileToDataUrl = (f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(f);
  });

  const handleUpload = async (docSpec, file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error('File troppo grande (max 20MB)'); return; }
    setUploading(prev => ({ ...prev, [docSpec.key]: true }));
    const tId = toast.loading(`Caricamento ${docSpec.label}…`);
    try {
      const dataUrl = await fileToDataUrl(file);
      const res = await api.publicUploadOnboarding(token, {
        key: docSpec.key, label: docSpec.label,
        data_url: dataUrl, file_name: file.name, file_type: docSpec.file_type,
      });
      setDocs(prev => ({ ...prev, [docSpec.key]: { url: res.url, file_name: file.name } }));
      toast.success('Caricato', { id: tId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload fallito', { id: tId });
    } finally {
      setUploading(prev => { const n = { ...prev }; delete n[docSpec.key]; return n; });
    }
  };

  // ------ Validazione step ------
  const validateStep = (idx) => {
    const s = steps[idx];
    if (!s) return { ok: true };
    if (s.kind === 'section') {
      const missing = (s.section.fields || []).filter(f => f.required && !(answers[f.key] || '').toString().trim());
      if (missing.length) return { ok: false, msg: `Compila i campi obbligatori: ${missing.map(m => m.label).join(', ')}` };
    }
    if (s.kind === 'docs') {
      const required = (data.required_docs || []).filter(d => d.required);
      const missing = required.filter(d => !docs[d.key]);
      if (missing.length) return { ok: false, msg: `Carica i documenti obbligatori: ${missing.map(m => m.label).join(', ')}` };
    }
    if (s.kind === 'review') {
      if (!acceptDeclaration) return { ok: false, msg: 'Devi accettare le dichiarazioni finali per inviare la candidatura.' };
    }
    return { ok: true };
  };

  const goNext = () => {
    const v = validateStep(stepIdx);
    if (!v.ok) { toast.error(v.msg); return; }
    if (stepIdx < total - 1) setStepIdx(stepIdx + 1);
  };
  const goBack = () => { if (stepIdx > 0) setStepIdx(stepIdx - 1); };
  const goTo = (idx) => {
    // Permetto di tornare indietro liberamente; per andare avanti deve validare gli step precedenti
    if (idx < stepIdx) { setStepIdx(idx); return; }
    if (idx === stepIdx) return;
    for (let i = stepIdx; i < idx; i++) {
      const v = validateStep(i);
      if (!v.ok) { toast.error(v.msg); return; }
    }
    setStepIdx(idx);
  };

  const submit = async () => {
    const v = validateStep(stepIdx);
    if (!v.ok) { toast.error(v.msg); return; }
    setSubmitting(true);
    try {
      await api.publicSubmitOnboarding(token, { submitted_data: answers });
      setSubmitted(true);
      toast.success('Candidatura inviata con successo!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio', { duration: 6000 });
    } finally { setSubmitting(false); }
  };

  // ------ Stati caricamento / errore / successo ------
  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-secondary text-muted-foreground">
      <div className="flex items-center gap-2"><Loader2 className="size-4 animate-spin" /> Caricamento…</div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen grid place-items-center bg-secondary p-6 text-center">
      <div>
        <div className="text-lg font-semibold">Link non valido</div>
        <div className="text-sm text-muted-foreground">Il link di onboarding è scaduto o già utilizzato. Contatta Scintilla Hub.</div>
      </div>
    </div>
  );
  if (submitted) return (
    <div className="min-h-screen grid place-items-center bg-secondary p-6">
      <Card className="max-w-lg w-full">
        <CardContent className="pt-6 text-center space-y-3">
          <CheckCircle2 className="size-12 text-primary mx-auto" />
          <div className="text-xl font-semibold">Candidatura ricevuta!</div>
          <div className="text-sm text-muted-foreground">
            Grazie. La nostra amministrazione revisionerà la tua candidatura e ti contatterà a breve
            all'indirizzo <strong>{data.invited_email}</strong> per gli step successivi.
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-secondary p-4 md:p-8">
      <Toaster position="top-center" />
      <div ref={topRef} className="max-w-3xl mx-auto space-y-4">

        {/* Header */}
        <div className="text-center py-2 md:py-4">
          <Sparkles className="size-9 text-primary mx-auto mb-2" />
          <h1 className="text-2xl md:text-3xl font-semibold">Candidatura Partner Scintilla Hub</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Segui i passaggi per completare la tua candidatura. Ti guideremo passo dopo passo.
          </p>
        </div>

        {/* Progress bar + stepper */}
        <Card className="overflow-hidden">
          <CardContent className="pt-4 pb-3 space-y-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div data-testid="step-indicator">
                <span className="font-medium text-foreground">Step {stepIdx + 1}</span> di {total}
                <span className="mx-2">•</span>
                <span className="text-primary font-medium">{current?.title}</span>
              </div>
              <div className="font-medium text-foreground" data-testid="step-percent">{percent}%</div>
            </div>
            <Progress value={percent} className="h-2" />
            <StepperDots steps={steps} stepIdx={stepIdx} goTo={goTo} />
          </CardContent>
        </Card>

        {/* Contenuto step */}
        {current?.kind === 'welcome' && (
          <WelcomeStep companyEmail={data.invited_email} />
        )}

        {current?.kind === 'section' && (
          <Card>
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center gap-2">
                <current.icon className="size-4 text-primary" />
                <div className="text-base font-semibold text-primary">{current.section.section}</div>
              </div>
              <Separator />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {current.section.fields.map(f => (
                  <div key={f.key} className={f.type === 'textarea' ? 'md:col-span-2' : ''}>
                    <Label className="text-xs">
                      {f.label}{f.required && <span className="text-destructive"> *</span>}
                    </Label>
                    {f.type === 'textarea' ? (
                      <Textarea
                        rows={2}
                        value={answers[f.key] || ''}
                        onChange={e => setAnswers(a => ({ ...a, [f.key]: e.target.value }))}
                        data-testid={`field-${f.key}`}
                      />
                    ) : (
                      <Input
                        type={f.type === 'email' ? 'email' : 'text'}
                        value={answers[f.key] || ''}
                        onChange={e => setAnswers(a => ({ ...a, [f.key]: e.target.value }))}
                        data-testid={`field-${f.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {current?.kind === 'docs' && (
          <Card>
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center gap-2">
                <FileCheck2 className="size-4 text-primary" />
                <div className="text-base font-semibold text-primary">Documenti richiesti</div>
              </div>
              <div className="text-xs text-muted-foreground -mt-1">
                Carica i file richiesti. Formati accettati: PDF per documenti, immagini per foto/loghi. Max 20MB per file.
              </div>
              <Separator />
              <div className="space-y-2">
                {(data.required_docs || []).map(d => (
                  <div key={d.key} className="border rounded-lg p-3 flex items-center gap-3 hover:bg-muted/40 transition-colors">
                    {d.file_type === 'pdf'
                      ? <FileText className="size-4 text-muted-foreground shrink-0" />
                      : <Camera className="size-4 text-muted-foreground shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        {d.label}{d.required && <span className="text-destructive"> *</span>}
                      </div>
                      {docs[d.key] && (
                        <div className="text-xs text-primary truncate flex items-center gap-1">
                          <CheckCircle2 className="size-3" /> {docs[d.key].file_name}
                        </div>
                      )}
                    </div>
                    <input
                      ref={el => { inputs.current[d.key] = el; }}
                      type="file"
                      className="hidden"
                      accept={d.file_type === 'pdf' ? '.pdf' : d.file_type === 'foto' ? 'image/*' : 'image/*,.pdf'}
                      onChange={e => handleUpload(d, e.target.files?.[0])}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => inputs.current[d.key]?.click()}
                      disabled={!!uploading[d.key]}
                      data-testid={`upload-${d.key}`}
                    >
                      {uploading[d.key] ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
                      <span className="ml-1">{docs[d.key] ? 'Sostituisci' : 'Carica'}</span>
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {current?.kind === 'review' && (
          <ReviewStep
            data={data}
            answers={answers}
            docs={docs}
            accept={acceptDeclaration}
            setAccept={setAcceptDeclaration}
            goTo={goTo}
            steps={steps}
          />
        )}

        {/* Navigation buttons */}
        <div className="flex items-center justify-between pt-1 pb-8 gap-3">
          <Button
            variant="outline"
            onClick={goBack}
            disabled={stepIdx === 0 || submitting}
            data-testid="wizard-back"
          >
            <ArrowLeft className="size-4 mr-1" /> Indietro
          </Button>

          {stepIdx < total - 1 ? (
            <Button onClick={goNext} data-testid="wizard-next">
              Avanti <ArrowRight className="size-4 ml-1" />
            </Button>
          ) : (
            <Button
              size="lg"
              onClick={submit}
              disabled={submitting}
              data-testid="submit-onboarding"
            >
              {submitting ? <Loader2 className="size-4 mr-2 animate-spin" /> : <CheckCircle2 className="size-4 mr-2" />}
              Invia candidatura
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * Sotto-componenti
 * ============================================================ */

function StepperDots({ steps, stepIdx, goTo }) {
  return (
    <div className="flex items-center justify-between gap-1 overflow-x-auto no-scrollbar">
      {steps.map((s, i) => {
        const isDone = i < stepIdx;
        const isCurrent = i === stepIdx;
        const Icon = s.icon || Sparkles;
        return (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            title={s.title}
            data-testid={`stepper-dot-${i}`}
            className={[
              'flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] shrink-0 transition-colors',
              isCurrent
                ? 'bg-primary text-primary-foreground border-primary'
                : isDone
                ? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20'
                : 'bg-background text-muted-foreground border-border hover:border-primary/40',
            ].join(' ')}
          >
            <span className={[
              'inline-flex items-center justify-center rounded-full size-5 text-[10px] font-semibold',
              isCurrent ? 'bg-primary-foreground text-primary'
                : isDone ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground',
            ].join(' ')}>
              {isDone ? <CheckCircle2 className="size-3" /> : i + 1}
            </span>
            <span className="hidden sm:inline whitespace-nowrap">
              <Icon className="size-3 inline mr-1 -mt-0.5" />
              {s.title}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function WelcomeStep({ companyEmail }) {
  const items = [
    { icon: Users, title: 'Referente di progetto', desc: 'Nome, ruolo, email e cellulare della persona che sarà sempre in contatto con noi.' },
    { icon: Building2, title: 'Dati anagrafici azienda', desc: 'Ragione sociale, P.IVA/CF, sede legale, PEC, SDI, REA, legale rappresentante.' },
    { icon: Palette, title: 'Loghi & Palette colori', desc: 'Logo principale, versioni negative/orizzontali e codici HEX dei colori aziendali.' },
    { icon: FileCheck2, title: 'Documenti', desc: 'Visura camerale, documento d\'identità del legale rappresentante e altri allegati richiesti.' },
  ];
  return (
    <Card>
      <CardContent className="pt-4 space-y-4">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-4 text-primary" />
          <div className="text-base font-semibold text-primary">Benvenuti nella candidatura Partner</div>
        </div>
        <p className="text-sm text-muted-foreground">
          Per garantirti un'integrazione fluida e rapida, ti chiederemo alcune informazioni sulla tua azienda
          e alcuni documenti obbligatori. Una compilazione completa e accurata ci permetterà di collaborare
          al meglio fin dal primo giorno.
        </p>

        <div className="rounded-lg border bg-muted/40 p-3 flex items-start gap-2">
          <Info className="size-4 text-primary mt-0.5 shrink-0" />
          <div className="text-xs text-muted-foreground">
            Al termine invieremo una notifica di ricezione a <strong className="text-foreground">{companyEmail}</strong>.
            Puoi tornare indietro in qualsiasi momento per correggere i dati inseriti.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.map((it, i) => (
            <div key={i} className="border rounded-lg p-3 flex items-start gap-3 hover:border-primary/40 transition-colors">
              <div className="rounded-md bg-primary/10 text-primary p-2 shrink-0">
                <it.icon className="size-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium">{it.title}</div>
                <div className="text-xs text-muted-foreground">{it.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-xs text-muted-foreground">
          Tempo stimato di compilazione: <strong className="text-foreground">5–10 minuti</strong>.
          Consigliamo di avere a portata di mano visura, documenti d'identità e loghi in formato vettoriale.
        </div>
      </CardContent>
    </Card>
  );
}

function ReviewStep({ data, answers, docs, accept, setAccept, goTo, steps }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="size-4 text-primary" />
            <div className="text-base font-semibold text-primary">Riepilogo della candidatura</div>
          </div>
          <div className="text-xs text-muted-foreground -mt-1">
            Controlla i dati inseriti prima di inviare. Clicca su una sezione per tornare a modificarla.
          </div>
          <Separator />
          {(data.questions || []).map((section) => {
            const stepIndexOfSection = steps.findIndex(s => s.kind === 'section' && s.section.section === section.section);
            return (
              <div key={section.section} className="border rounded-lg p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-sm font-semibold text-primary">{section.section}</div>
                  <Button variant="ghost" size="sm" onClick={() => goTo(stepIndexOfSection)} data-testid={`edit-section-${stepIndexOfSection}`}>
                    Modifica
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                  {section.fields.map(f => (
                    <div key={f.key} className="text-xs">
                      <span className="text-muted-foreground">{f.label}: </span>
                      <span className="font-medium break-words">{answers[f.key] || <em className="text-muted-foreground">—</em>}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="border rounded-lg p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-sm font-semibold text-primary">Documenti caricati</div>
              <Button variant="ghost" size="sm" onClick={() => goTo(steps.findIndex(s => s.kind === 'docs'))} data-testid="edit-section-docs">
                Modifica
              </Button>
            </div>
            <div className="space-y-1">
              {(data.required_docs || []).map(d => (
                <div key={d.key} className="text-xs flex items-center gap-2">
                  {docs[d.key]
                    ? <CheckCircle2 className="size-3.5 text-primary" />
                    : <span className="size-3.5 rounded-full border border-dashed border-muted-foreground/40" />}
                  <span className="text-muted-foreground">{d.label}:</span>
                  <span className="font-medium truncate">
                    {docs[d.key]?.file_name || <em className="text-muted-foreground">non caricato</em>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-4 text-primary" />
            <div className="text-base font-semibold text-primary">Dichiarazioni finali</div>
          </div>
          <div className="text-xs text-muted-foreground space-y-2">
            <p>
              Il <strong>Partner</strong> che compila la presente candidatura, in persona del proprio Legale Rappresentante,
              garantisce sotto la propria responsabilità civile e penale:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Di essere in pieno possesso di tutte le creatività, dei loghi e di quant'altro serva per la parte Marketing, autorizzazioni, ecc.</li>
              <li>Di impegnarsi formalmente ad inserire nei contratti stipulati con i clienti finali la clausola di subappalto obbligatoria a favore di <strong>SCINTILLA HUB S.r.l.</strong>, come tassativamente richiesto dall'Art. 16.5 del Contratto, accettando l'obbligo di manleva in caso di omissione.</li>
            </ul>
          </div>

          <div className="flex items-start gap-2 rounded-lg border p-3 bg-muted/30">
            <Checkbox
              id="accept-declaration"
              checked={accept}
              onCheckedChange={(v) => setAccept(!!v)}
              data-testid="accept-declaration"
            />
            <label htmlFor="accept-declaration" className="text-xs cursor-pointer">
              Dichiaro di aver letto e di accettare le dichiarazioni sopra riportate, confermando che i dati e i documenti
              forniti sono veritieri e completi.
            </label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* Helpers */
function pickSectionIcon(name = '', i = 0) {
  const n = name.toLowerCase();
  if (n.includes('anagraf') || n.includes('azienda') || n.includes('dati')) return Building2;
  if (n.includes('referen') || n.includes('contatto') || n.includes('utente') || n.includes('crm')) return Users;
  if (n.includes('logo') || n.includes('color') || n.includes('brand') || n.includes('marketing')) return Palette;
  if (n.includes('doc')) return FileCheck2;
  const rot = [Building2, Users, Palette, ClipboardList];
  return rot[i % rot.length];
}
